/**
 * Featured Blogs Component
 * Minimal list of recent blog posts on the home page
 */
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { useCustomTranslation } from '../../hooks';

const recentPosts = [
    {
        slug: 'shorter-vs-longer-mortgage-term-hong-kong',
        title: 'Shorter or Longer Mortgage Term?',
        titleZh: '按揭年期揀短定長？',
        description: 'Compare monthly payment and total interest across 15 to 30 years.',
        descriptionZh: '比較15至30年期的月供與總利息。',
        date: '2026-10-09',
        category: 'Mortgage',
    },
    {
        slug: 'how-much-can-you-borrow-hk10000-monthly-mortgage',
        title: 'How Much Can You Borrow on HK$10,000 a Month?',
        titleZh: '月供$10,000可以借幾多？',
        description: 'Work backwards from a monthly payment to a loan amount.',
        descriptionZh: '由每月供款反推貸款額，了解借貸能力。',
        date: '2026-10-07',
        category: 'Mortgage',
    },
    {
        slug: 'hong-kong-mortgage-amortization-explained',
        title: 'Mortgage Amortization, Explained',
        titleZh: '一文看懂按揭攤還',
        description: 'See how each payment is split between mortgage interest and principal.',
        descriptionZh: '看清每月供款如何分配至按揭利息與本金。',
        date: '2026-10-06',
        category: 'Mortgage',
    },
    {
        slug: '5-mortgage-mistakes-first-time-buyers-hong-kong',
        title: '5 Mortgage Mistakes First-Time Buyers Make',
        titleZh: '首次買樓5大按揭錯誤',
        description: 'Avoid costly mistakes: stress tests, hidden fees, rate comparisons, and more.',
        descriptionZh: '避開代價高昂的錯誤：壓力測試、隱藏收費、利率比較等。',
        date: '2026-05-18',
        category: 'Property',
    },
    {
        slug: 'emergency-fund-how-much-hong-kong',
        title: 'Emergency Fund: How Much is Enough?',
        titleZh: '緊急儲備金應準備多少？',
        description: 'Practical guide: 3-6 months of living expenses. Learn how much, where to keep it, with real examples.',
        descriptionZh: '實用指南：準備 3-6 個月生活費。了解金額、存放位置及真實例子。',
        date: '2026-05-10',
        category: 'Finance',
    },
    {
        slug: 'effective-interest-rate-vs-flat-rate-hong-kong',
        title: 'Effective Interest Rate vs Flat Rate',
        titleZh: '實際年利率 vs 平息',
        description: 'Learn the crucial difference between APR and flat rates, avoid common loan traps.',
        descriptionZh: '了解APR與平息的重要分別，避開常見貸款陷阱。',
        date: '2026-04-21',
        category: 'Loans',
    },
    {
        slug: 'how-to-read-mortgage-statement-hong-kong',
        title: 'How to Read Your Mortgage Statement',
        titleZh: '按揭月結單全解讀',
        description: 'Understand every number — principal, interest, balance, and penalty status.',
        descriptionZh: '了解每個數字——本金、利息、結餘及罰息期狀態。',
        date: '2026-04-12',
        category: 'Mortgage',
    },
    {
        slug: 'hos-mortgage-guide-hong-kong-2026',
        title: 'HOS Mortgage Guide 2026',
        titleZh: '居屋按揭指南 2026',
        description: 'Green Form vs White Form, LTV ratios, guarantee periods, and payment examples.',
        descriptionZh: '綠表 vs 白表、按揭成數、擔保期及供款例子。',
        date: '2026-04-10',
        category: 'HOS',
    },
];

export default function FeaturedBlogs() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    return (
        <Box sx={{ py: { xs: 6, md: 8 }, borderTop: '1px solid #f1f5f9' }}>
            <Container maxWidth="md">
                <Typography
                    sx={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 1.5,
                        color: '#667eea',
                        mb: 1,
                    }}
                >
                    {isEnglish ? 'Latest Articles' : '最新文章'}
                </Typography>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        fontWeight: 700,
                        color: '#1a1a2e',
                        mb: 4,
                    }}
                >
                    {isEnglish ? 'Financial Insights' : '理財資訊'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {recentPosts.map((post) => (
                        <Box
                            key={post.slug}
                            component={Link}
                            to={`/blog/${post.slug}`}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 3,
                                py: 3,
                                borderBottom: '1px solid #f1f5f9',
                                textDecoration: 'none',
                                color: 'inherit',
                                transition: 'background 0.2s',
                                mx: -2,
                                px: 2,
                                borderRadius: 1,
                                '&:hover': { bgcolor: '#f8fafc' },
                                '&:hover .arrow': { transform: 'translateX(4px)', color: '#667eea' },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#667eea', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {post.category}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                        {new Date(post.date).toLocaleDateString(isEnglish ? 'en-US' : 'zh-HK', { month: 'short', day: 'numeric' })}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, fontWeight: 600, color: '#1a1a2e', mb: 0.5 }}>
                                    {isEnglish ? post.title : post.titleZh}
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', display: { xs: 'none', sm: 'block' } }}>
                                    {isEnglish ? post.description : post.descriptionZh}
                                </Typography>
                            </Box>
                            <ArrowIcon className="arrow" sx={{ fontSize: '1.25rem', color: '#cbd5e1', transition: 'all 0.2s', flexShrink: 0 }} />
                        </Box>
                    ))}
                </Box>

                <Box sx={{ mt: 4 }}>
                    <Button
                        component={Link}
                        to="/blog"
                        endIcon={<ArrowIcon sx={{ fontSize: '1rem !important' }} />}
                        sx={{ color: '#667eea', fontWeight: 600, textTransform: 'none', px: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                    >
                        {isEnglish ? 'View all articles' : '查看所有文章'}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}
